import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { Navbar, Footer } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { getUTMs } from '@/utils/utm';
import {
  Lock, FileText, CheckCircle, ExternalLink,
  Hammer, FlaskConical, Landmark, Activity, Sigma, GitBranch, Sparkles
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const INTEGRATIONS = [
  {
    icon: Activity,
    title: '1. Checkpoint-as-Outcome-Signal',
    body: 'Pipe Kojiru Checkpoint events into Engine as labeled production outcomes. Clustering moves from "trace patterns precede error logs" to "trace patterns precede a $40K default" — the most expensive missing signal in the agent-tooling stack.',
    accent: 'text-amber-300',
  },
  {
    icon: Sigma,
    title: '2. ACS-Δ as a Top-Level Eval',
    body: 'A new eval primitive: "This change must not reduce the deployed agent\u2019s projected ACS\u2122 by more than X points." Engineering ships nothing that downgrades the agent\u2019s bankability.',
    accent: 'text-emerald-300',
  },
  {
    icon: FlaskConical,
    title: '3. One-Click Sandbox Credit Lines',
    body: 'Button inside LangSmith: deploy this agent to a Kojiru sandbox credit line. $1K line on Base testnet, 30 days, every action becomes a Checkpoint, every Checkpoint flows back into Engine as labeled outcome data.',
    accent: 'text-cyan-300',
  },
  {
    icon: GitBranch,
    title: '4. Trace Hygiene \u2192 Credit Worthiness',
    body: 'LangSmith error rate, fix-cycle time, and eval coverage feed Kojiru\u2019s ACS underwriting model. Agents with great hygiene get cheaper capital. First time in market that "good engineering" is literally bankable.',
    accent: 'text-purple-300',
  },
  {
    icon: Sparkles,
    title: '5. Fix Proposals \u2192 Risk Simulator',
    body: 'When Engine drafts a fix, run it through Kojiru\u2019s Monte Carlo Yield Simulator first. "This fix reduces 90-day default probability from 8.2% to 3.7%, raises median lender yield by 14%." Engineering fixes now have a dollar value.',
    accent: 'text-amber-300',
  },
  {
    icon: Landmark,
    title: '6. Joint Narrative — "Build. Test. Bank."',
    body: 'LangSmith is how you build an agent. Kojiru is how you bank one. The first complete agent reliability stack \u2014 a co-marketed story neither side could buy alone.',
    accent: 'text-emerald-300',
  },
];

export default function LangChain() {
  const [params] = useSearchParams();
  const sourceParam = params.get('src') || params.get('source') || 'langchain-landing';

  const utm = useMemo(() => {
    // getUTMs() reads current URL params first, then falls back to the
    // 30-day localStorage snapshot captured by App.js on the ad landing.
    // This is what makes cross-page attribution work.
    return getUTMs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const [form, setForm] = useState({ name: '', email: '', company: '', role: '', intent: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState(null);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/bd/langchain/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          company: form.company.trim() || null,
          role: form.role.trim() || null,
          intent: form.intent.trim() || null,
          source: sourceParam,
          utm,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        const detail = typeof data?.detail === 'string'
          ? data.detail
          : Array.isArray(data?.detail) ? data.detail.map((d) => d.msg).join(', ')
          : 'Something went wrong. Please try again.';
        throw new Error(detail);
      }
      const fullUrl = `${API_URL}${data.download_url}`;
      setDownloadUrl(fullUrl);
      window.open(fullUrl, '_blank', 'noopener');
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="langchain-page">
      <Navbar />

      {/* ── HERO ── */}
      <section className="pt-32 pb-12 px-6 border-b border-white/[0.06]">
        <div className="container mx-auto max-w-6xl">
          <div className="flex items-center gap-2 mb-6 text-[11px] font-mono tracking-[0.22em] text-emerald-400">
            <span className="px-2 py-1 rounded-md bg-emerald-500/10 border border-emerald-500/20">
              INTEGRATION BRIEF · FOR LANGCHAIN
            </span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] max-w-4xl">
            <span className="text-amber-300">Build.</span>{' '}
            <span className="text-cyan-300">Test.</span>{' '}
            <span className="text-emerald-300">Bank.</span>
          </h1>
          <p className="text-base sm:text-lg text-zinc-400 max-w-3xl mt-5 leading-relaxed">
            LangSmith Engine optimizes for engineering quality. It does not know when an agent
            earned money or lost it. <span className="text-white font-semibold">Kojiru produces that signal</span> — structured,
            on-chain, deterministic. Together: the first complete agent reliability stack.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-10" data-testid="lc-hero-stack">
            {[
              { label: 'BUILD', sub: 'LangChain + LangSmith', icon: Hammer, color: 'border-amber-500/30 bg-amber-500/[0.04]', accent: 'text-amber-300' },
              { label: 'TEST',  sub: 'LangSmith Engine · traces, evals, fixes', icon: FlaskConical, color: 'border-cyan-500/30 bg-cyan-500/[0.04]', accent: 'text-cyan-300' },
              { label: 'BANK',  sub: 'Kojiru · (ACS)™, Checkpoints, USDC settlement', icon: Landmark, color: 'border-emerald-500/30 bg-emerald-500/[0.04]', accent: 'text-emerald-300' },
            ].map((s) => (
              <div key={s.label} className={`p-5 rounded-2xl border ${s.color}`}>
                <s.icon className={`w-6 h-6 ${s.accent} mb-3`} />
                <div className={`text-[10px] font-mono tracking-[0.22em] uppercase mb-1 ${s.accent}`}>{s.label}</div>
                <div className="text-sm text-zinc-300 leading-relaxed">{s.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── INTEGRATION IDEAS ── */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="mb-10">
            <div className="text-[11px] font-mono tracking-[0.22em] text-cyan-400 mb-3">// SIX CONCRETE INTEGRATIONS</div>
            <h2 className="text-2xl sm:text-3xl font-bold">
              The missing outcome signal, six ways.
            </h2>
            <p className="text-zinc-400 mt-3 max-w-2xl">
              Each is shippable inside one release cycle. Our LangChain SDK wrapper is already coded. Integration is roughly 300 lines on your side.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="lc-integration-grid">
            {INTEGRATIONS.map((it, i) => (
              <motion.div
                key={it.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.07] hover:border-white/15 transition-all"
                data-testid={`lc-integration-card-${i+1}`}
              >
                <it.icon className={`w-6 h-6 ${it.accent} mb-3`} />
                <h3 className="text-base font-semibold mb-2 leading-snug">{it.title}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{it.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── LEAD-CAPTURE GATE ── */}
      <section className="py-16 px-6 border-t border-white/[0.06] bg-gradient-to-b from-transparent via-emerald-500/[0.025] to-transparent">
        <div className="container mx-auto max-w-5xl">
          <div className="grid md:grid-cols-5 gap-10 items-start">
            <div className="md:col-span-2">
              <div className="text-[11px] font-mono tracking-[0.22em] text-emerald-400 mb-3">// FULL BRIEF</div>
              <h2 className="text-2xl sm:text-3xl font-bold mb-4 leading-tight">
                Get the 1-page Integration Brief.
              </h2>
              <p className="text-sm text-zinc-400 leading-relaxed mb-5">
                Six integration ideas, the missing-outcome-signal frame, and a draft 25-minute demo agenda \u2014 in a clean institutional PDF you can forward to your team.
              </p>
              <ul className="space-y-2.5 text-sm text-zinc-300">
                {[
                  'BaseScan source-verified on Base — formal verification on Q3 2026 roadmap',
                  'Live Monte Carlo Yield Simulator',
                  'Plug-and-play LangChain SDK wrapper',
                  'GENIUS Act\u2013aligned framing',
                ].map((p) => (
                  <li key={p} className="flex gap-2.5">
                    <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="md:col-span-3">
              <div className="p-6 rounded-2xl bg-white/[0.03] border border-white/[0.08]" data-testid="lc-lead-form-card">
                <div className="flex items-center gap-2 text-[11px] font-mono tracking-[0.22em] text-emerald-400 mb-4">
                  <Lock className="w-3.5 h-3.5" /> GATED ASSET · EMAIL REQUIRED
                </div>

                {downloadUrl ? (
                  <div className="space-y-4" data-testid="lc-success-panel">
                    <div className="flex items-center gap-2 text-emerald-300 font-semibold">
                      <CheckCircle className="w-5 h-5" /> Brief sent. Check your downloads.
                    </div>
                    <p className="text-sm text-zinc-400">
                      If your browser blocked the popup, use this link \u2014 it expires in 24 hours.
                    </p>
                    <a
                      href={downloadUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-500 text-black font-semibold text-sm hover:brightness-110 transition"
                      data-testid="lc-download-link"
                    >
                      <FileText className="w-4 h-4" />
                      Download Integration Brief (PDF)
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4" data-testid="lc-lead-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Input label="Full name" required value={form.name} onChange={update('name')} testId="lc-form-name" />
                      <Input label="Work email" type="email" required value={form.email} onChange={update('email')} testId="lc-form-email" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Input label="Company" value={form.company} onChange={update('company')} testId="lc-form-company" />
                      <Input label="Role" value={form.role} onChange={update('role')} testId="lc-form-role" />
                    </div>
                    <Input
                      label="What integration are you most interested in? (optional)"
                      value={form.intent}
                      onChange={update('intent')}
                      testId="lc-form-intent"
                    />
                    {error && (
                      <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid="lc-form-error">
                        {error}
                      </div>
                    )}
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-emerald-500 hover:bg-emerald-600 text-black font-semibold"
                      data-testid="lc-form-submit"
                    >
                      {submitting ? 'Sending\u2026' : 'Get the Integration Brief (PDF)'}
                    </Button>
                    <p className="text-[11px] text-zinc-500 leading-relaxed">
                      We store your email to send the brief and (occasionally) follow up. No third-party sharing.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function Input({ label, value, onChange, type = 'text', required = false, testId }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-mono tracking-wider uppercase text-zinc-400 mb-1.5">{label}</span>
      <input
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testId}
        className="w-full px-3 py-2.5 rounded-lg bg-black/40 border border-white/[0.08] focus:border-emerald-500/60 focus:outline-none text-sm text-white placeholder-zinc-600"
      />
    </label>
  );
}
